import DSDatePicker from "../scot_gov/components/date-picker/date-picker";

/**
 * Usage:
 * <delta-date-picker label="Payment date" name="paymentDate" required
 *                    value="2026-10-22" min-date="2026-01-01" max-date="2026-12-31">
 *     <unavailable-dates>
 *         <time datetime="2026-10-20"></time>
 *         <time datetime="2026-10-21"></time>
 *     </unavailable-dates>
 * </delta-date-picker>
 *
 * Dates use YYYY-MM-DD. Only name is needed to submit the value with a form;
 * label defaults to "Date". All other attributes and unavailable-dates are optional.
 * Displays separate day/month/year boxes and submits a hidden YYYY-MM-DD value.
 * Use submit-format="dd/MM/yyyy" for forms that expect that format.
 * Use disabled for a read-only picker, and start-date-picker/end-date-picker
 * to constrain this picker to another picker's selected date.
 * Invalid initial values leave the boxes blank. For the default submit format,
 * unavailable or out-of-range initial values also leave the boxes blank.
 * Attributes and unavailable dates are read once when the element is initialised;
 * changing them after rendering does not update the picker.
 */
class DeltaDatePicker extends HTMLElement {
    private datePicker: DSDatePicker | null = null;
    private static nextId = 0;
    private selectedDate: Date | null = null;
    private refreshLimits: (() => void) | null = null;
    private readonly onLinkedDateChange = (event: Event): void => {
        const peer = event.target;
        if (peer instanceof DeltaDatePicker && peer !== this &&
            (peer.id === this.getAttribute("start-date-picker") || peer.id === this.getAttribute("end-date-picker"))) {
            this.refreshLimits?.();
        }
    };

    disconnectedCallback(): void {
        document.removeEventListener("date-change", this.onLinkedDateChange);
    }

    connectedCallback(): void {
        document.addEventListener("date-change", this.onLinkedDateChange);
        if (this.datePicker) {
            this.refreshLimits?.();
            return;
        }
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => {
                if (this.isConnected) {
                    this.connectedCallback();
                }
            }, { once: true });
            return;
        }

        const labelText = this.getAttribute("label") ?? "Date";
        const disabledDates = this.getUnavailableDates();
        let minDate = this.parseDate(this.getAttribute("min-date"));
        let maxDate = this.parseDate(this.getAttribute("max-date"));
        const fixedMinDate = minDate;
        const fixedMaxDate = maxDate;
        const inputId = `${this.id || `delta-date-picker-${++DeltaDatePicker.nextId}`}-input`;
        const fieldset = document.createElement("fieldset");
        fieldset.className = "govuk-fieldset";
        fieldset.disabled = this.hasAttribute("disabled");
        fieldset.setAttribute("role", "group");
        fieldset.setAttribute("aria-describedby", `${inputId}-hint`);
        const legend = document.createElement("legend");
        legend.className = "govuk-fieldset__legend";
        legend.textContent = labelText;

        const wrapper = document.createElement("div");
        wrapper.className = "ds_datepicker ds_datepicker--multiple";
        const inputWrapper = document.createElement("div");
        inputWrapper.className = "ds_datepicker__input-wrapper";
        const hint = document.createElement("div");
        hint.id = `${inputId}-hint`;
        hint.className = "govuk-hint";
        hint.textContent = "For example, 21 10 2026";

        const submittedInput = document.createElement("input");
        submittedInput.type = "hidden";
        submittedInput.id = inputId;
        submittedInput.name = this.getAttribute("name") ?? "";
        const submittedContainer = document.createElement("div");
        submittedContainer.hidden = true;
        submittedContainer.append(submittedInput);
        inputWrapper.append(submittedContainer);

        const inputs = ["day", "month", "year"].map((part, index) => {
            const item = document.createElement("div");
            const label = document.createElement("label");
            label.className = "govuk-label govuk-date-input__label";
            label.htmlFor = `${inputId}-${part}`;
            label.textContent = part[0].toUpperCase() + part.slice(1);
            const input = document.createElement("input");
            input.type = "text";
            input.inputMode = "numeric";
            input.id = label.htmlFor;
            input.className = `govuk-input govuk-date-input__input govuk-input--width-${index === 2 ? 4 : 2} js-datepicker-${index === 0 ? "date" : part}`;
            input.required = this.hasAttribute("required");
            input.setAttribute("aria-describedby", hint.id);
            item.append(label, input);
            inputWrapper.append(item);
            return input;
        });
        wrapper.append(inputWrapper);
        fieldset.append(legend, hint, wrapper);
        this.replaceChildren(fieldset);
        this.classList.add("delta-date-picker");

        const updateValue = (notify = true): void => {
            const [day, month, year] = inputs.map(input => input.value.trim());
            const isoDate = /^\d{1,2}$/.test(day) && /^\d{1,2}$/.test(month) && /^\d{4}$/.test(year)
                ? `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}` : null;
            const date = this.parseDate(isoDate);
            const previousDate = this.selectedDate?.getTime();
            this.selectedDate = date;
            const unavailable = date && (minDate && date < minDate || maxDate && date > maxDate ||
                disabledDates.some(disabled => disabled.getTime() === date.getTime()));
            inputs[0].setCustomValidity(inputs.some(input => input.value) && (!date || unavailable)
                ? "Enter an available date using the day, month and year boxes" : "");
            submittedInput.value = this.getAttribute("submit-format") === "dd/MM/yyyy"
                ? (day || month || year ? `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}` : "")
                : date && !unavailable ? isoDate! : "";
            if (notify && previousDate !== date?.getTime()) {
                this.dispatchEvent(new CustomEvent("date-change", {
                    bubbles: true,
                    detail: { value: isoDate, date: date ? new Date(date) : null }
                }));
            }
        };
        const handleInput = (): void => updateValue();
        inputs.forEach(input => {
            input.addEventListener("input", handleInput);
            input.addEventListener("change", handleInput);
        });
        submittedInput.addEventListener("change", handleInput);

        this.datePicker = new DSDatePicker(wrapper, {
            minDate,
            maxDate,
            disabledDates,
            imagePath: "/public/images/icons/date-picker/",
            dialogId: `${inputId}-dialog`
        });
        this.render();
        const initialDate = this.parseDate(this.getAttribute("value"));
        if (initialDate && (this.getAttribute("submit-format") === "dd/MM/yyyy" || !this.datePicker.isDisabledDate(initialDate))) {
            this.datePicker.setDate(initialDate);
            this.datePicker.inputDate = initialDate;
            this.datePicker.currentDate = new Date(initialDate);
            updateValue();
        }
        wrapper.querySelector(".js-calendar-button")?.classList.add("govuk-button");
        this.refreshLimits = (): void => {
            const start = document.getElementById(this.getAttribute("start-date-picker") ?? "");
            const end = document.getElementById(this.getAttribute("end-date-picker") ?? "");
            const linkedMin = start instanceof DeltaDatePicker ? start.selectedDate : null;
            const linkedMax = end instanceof DeltaDatePicker ? end.selectedDate : null;
            minDate = linkedMin && (!fixedMinDate || linkedMin > fixedMinDate) ? linkedMin : fixedMinDate;
            maxDate = linkedMax && (!fixedMaxDate || linkedMax < fixedMaxDate) ? linkedMax : fixedMaxDate;
            this.datePicker!.minDate = minDate ?? undefined;
            this.datePicker!.maxDate = maxDate ?? undefined;
            updateValue(false);
            this.datePicker!.updateCalendar();
        };
        this.refreshLimits();
        queueMicrotask(() => {
            if (this.isConnected) {
                this.refreshLimits?.();
            }
        });
    }

    render(): void {
        this.datePicker?.init();
    }

    getUnavailableDates(): Date[] {
        return Array.from(this.querySelectorAll<HTMLTimeElement>("unavailable-dates time"))
            .map(time => this.parseDate(time.dateTime))
            .filter((date): date is Date => date !== null);
    }

    private parseDate(value: string | null): Date | null {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return null;
        }
        const [year, month, day] = value.split("-").map(Number);
        const date = new Date(0);
        date.setFullYear(year, month - 1, day);
        date.setHours(0, 0, 0, 0);
        return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
    }
}

if (!customElements.get("delta-date-picker")) {
    customElements.define("delta-date-picker", DeltaDatePicker);
}